/**
 * 门客面板组件
 * 显示门客管理和任务分配
 */

import { useState } from 'react'
import { useRetainerStore } from '../../stores/retainerStore'
import './RetainerPanel.css'

interface RetainerPanelProps {
  factionId: string
}

export function RetainerPanel({ factionId }: RetainerPanelProps) {
  const [activeTab, setActiveTab] = useState<'retainers' | 'missions' | 'recruitment' | 'events'>('retainers')
  const [selectedRetainer, setSelectedRetainer] = useState<string | null>(null)
  const [selectedMission, setSelectedMission] = useState<string | null>(null)
  
  const {
    retainers,
    missions,
    recruitmentPlans,
    events,
    hireRetainer,
    assignMission,
    createMission,
    completeMission,
    startRecruitment
  } = useRetainerStore()
  
  // 过滤当前势力的门客
  const factionRetainers = Array.from(retainers.values()).filter(retainer => retainer.factionId === factionId)
  
  // 过滤当前势力的招募计划
  const factionRecruitmentPlans = Array.from(recruitmentPlans.values()).filter(plan => plan.factionId === factionId)
  
  // 过滤当前势力的事件
  const factionEvents = Array.from(events.values()).filter(event => {
    const retainer = retainers.get(event.retainerId)
    return retainer?.factionId === factionId
  })
  
  // 处理招募门客
  const handleHireRetainer = () => {
    const retainerNames = ['姜子牙', '周公旦', '召公奭', '毕公高']
    const types: Array<'scholar' | 'warrior' | 'merchant' | 'strategist'> = ['strategist', 'scholar', 'merchant', 'warrior']
    
    retainerNames.forEach((name, index) => {
      hireRetainer(name, types[index], factionId)
    })
  }
  
  // 处理创建任务
  const handleCreateMission = () => {
    createMission('刺探商军情报', 'spy', '前往朝歌刺探商军的布防情况', 5)
    createMission('说服诸侯', 'negotiate', '说服附近的诸侯加入我们的联盟', 4)
    createMission('训练士兵', 'train', '在镐京训练新招募的士兵', 3)
  }
  
  // 处理开始招募
  const handleStartRecruitment = () => {
    startRecruitment(factionId, 'city_gaojing', 1000)
  }
  
  // 处理分配任务
  const handleAssignMission = (retainerId: string, missionId: string) => {
    assignMission(retainerId, missionId)
  }
  
  // 处理完成任务
  const handleCompleteMission = (missionId: string, success: boolean) => {
    completeMission(missionId, success)
  }
  
  return (
    <div className="retainer-panel">
      {/* 标签页 */}
      <div className="retainer-tabs">
        <button 
          className={`retainer-tab ${activeTab === 'retainers' ? 'active' : ''}`}
          onClick={() => setActiveTab('retainers')}
        >
          门客
        </button>
        <button 
          className={`retainer-tab ${activeTab === 'missions' ? 'active' : ''}`}
          onClick={() => setActiveTab('missions')}
        >
          任务
        </button>
        <button 
          className={`retainer-tab ${activeTab === 'recruitment' ? 'active' : ''}`}
          onClick={() => setActiveTab('recruitment')}
        >
          招募
        </button>
        <button 
          className={`retainer-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          事件
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="retainer-content">
        {/* 门客管理 */}
        {activeTab === 'retainers' && (
          <div className="retainers-section">
            <div className="section-header">
              <h3>门客管理</h3>
              <button 
                className="hire-retainer-btn"
                onClick={handleHireRetainer}
              >
                招募门客
              </button>
            </div>
            
            <div className="retainers-list">
              {factionRetainers.map(retainer => (
                <div 
                  key={retainer.id}
                  className={`retainer-card ${retainer.status} ${retainer.quality}`}
                  onClick={() => setSelectedRetainer(retainer.id)}
                >
                  <div className="retainer-header">
                    <div className="retainer-info">
                      <h4>{retainer.name}</h4>
                      <div className="retainer-meta">
                        <span className={`retainer-type ${retainer.type}`}>
                          {getRetainerTypeText(retainer.type)}
                        </span>
                        <span className={`retainer-quality ${retainer.quality}`}>
                          {getRetainerQualityText(retainer.quality)}
                        </span>
                        <span className={`retainer-status ${retainer.status}`}>
                          {getRetainerStatusText(retainer.status)}
                        </span>
                      </div>
                    </div>
                    <div className="retainer-level">
                      Lv.{retainer.level}
                    </div>
                  </div>
                  
                  <div className="retainer-attributes">
                    <h5>属性:</h5>
                    <div className="attributes-grid">
                      <div className="attribute-item">
                        <span>智力: {retainer.attributes.intelligence}</span>
                        <div className="attribute-bar">
                          <div 
                            className="attribute-fill"
                            style={{ width: `${retainer.attributes.intelligence}%` }}
                          />
                        </div>
                      </div>
                      <div className="attribute-item">
                        <span>力量: {retainer.attributes.strength}</span>
                        <div className="attribute-bar">
                          <div 
                            className="attribute-fill"
                            style={{ width: `${retainer.attributes.strength}%` }}
                          />
                        </div>
                      </div>
                      <div className="attribute-item">
                        <span>魅力: {retainer.attributes.charisma}</span>
                        <div className="attribute-bar">
                          <div 
                            className="attribute-fill"
                            style={{ width: `${retainer.attributes.charisma}%` }}
                          />
                        </div>
                      </div>
                      <div className="attribute-item">
                        <span>忠诚: {retainer.attributes.loyalty}</span>
                        <div className="attribute-bar">
                          <div 
                            className="attribute-fill loyalty"
                            style={{ width: `${retainer.attributes.loyalty}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="retainer-skills">
                    <h5>技能:</h5>
                    <div className="skills-list">
                      {retainer.skills.map(skill => (
                        <div key={skill.name} className="skill-item">
                          <span className="skill-name">{skill.name}</span>
                          <span className="skill-level">Lv.{skill.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {retainer.currentMissionId && (
                    <div className="retainer-mission">
                      <h5>当前任务:</h5>
                      <span>{missions.get(retainer.currentMissionId)?.name || '未知任务'}</span>
                    </div>
                  )}
                </div>
              ))}
              
              {factionRetainers.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">👤</span>
                  <p>暂无门客</p>
                  <button 
                    className="hire-retainer-btn small"
                    onClick={handleHireRetainer}
                  >
                    招募第一个门客
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 任务管理 */}
        {activeTab === 'missions' && (
          <div className="missions-section">
            <div className="section-header">
              <h3>任务管理</h3>
              <button 
                className="create-mission-btn"
                onClick={handleCreateMission}
              >
                创建任务
              </button>
            </div>
            
            <div className="missions-list">
              {Array.from(missions.values()).map(mission => (
                <div 
                  key={mission.id}
                  className={`mission-card ${mission.status}`}
                  onClick={() => setSelectedMission(mission.id)}
                >
                  <div className="mission-header">
                    <h4>{mission.name}</h4>
                    <span className={`mission-status ${mission.status}`}>
                      {getMissionStatusText(mission.status)}
                    </span>
                  </div>
                  <div className="mission-details">
                    <span>类型: {getMissionTypeText(mission.type)}</span>
                    <span>难度: {mission.difficulty}/10</span>
                    <span>持续时间: {mission.duration} 天</span>
                  </div>
                  <div className="mission-description">
                    <p>{mission.description}</p>
                  </div>
                  <div className="mission-rewards">
                    <h5>奖励:</h5>
                    <div className="rewards-list">
                      <span>经验: {mission.rewards.experience}</span>
                      <span>金钱: {mission.rewards.money}</span>
                      <span>声望: {mission.rewards.reputation}</span>
                    </div>
                  </div>
                  {mission.assigneeId && (
                    <div className="mission-assignee">
                      <h5>执行者:</h5>
                      <span>{retainers.get(mission.assigneeId)?.name || '未知'}</span>
                    </div>
                  )}
                  <div className="mission-actions">
                    {mission.status === 'available' && (
                      <div className="assign-section">
                        <h5>分配给:</h5>
                        <div className="retainer-selector">
                          {factionRetainers
                            .filter(r => r.status === 'idle')
                            .map(retainer => (
                              <button 
                                key={retainer.id}
                                className="assign-btn"
                                onClick={() => handleAssignMission(retainer.id, mission.id)}
                              >
                                {retainer.name}
                              </button>
                            ))
                          }
                        </div>
                      </div>
                    )}
                    {mission.status === 'in_progress' && (
                      <div className="complete-section">
                        <button 
                          className="complete-btn success"
                          onClick={() => handleCompleteMission(mission.id, true)}
                        >
                          任务成功
                        </button>
                        <button 
                          className="complete-btn failure"
                          onClick={() => handleCompleteMission(mission.id, false)}
                        >
                          任务失败
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {missions.size === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📜</span>
                  <p>暂无任务</p>
                  <button 
                    className="create-mission-btn small"
                    onClick={handleCreateMission}
                  >
                    创建第一个任务
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 招募管理 */}
        {activeTab === 'recruitment' && (
          <div className="recruitment-section">
            <div className="section-header">
              <h3>招募管理</h3>
              <button 
                className="start-recruitment-btn"
                onClick={handleStartRecruitment}
              >
                开始招募
              </button>
            </div>
            
            <div className="recruitment-list">
              {factionRecruitmentPlans.map(plan => (
                <div key={plan.id} className={`recruitment-card ${plan.status}`}>
                  <div className="recruitment-header">
                    <h4>招募计划 #{plan.id.slice(-4)}</h4>
                    <span className={`recruitment-status ${plan.status}`}>
                      {plan.status === 'in_progress' ? '进行中' : plan.status === 'completed' ? '已完成' : '已取消'}
                    </span>
                  </div>
                  <div className="recruitment-details">
                    <span>地点: {plan.location}</span>
                    <span>预算: {plan.budget.money} 金钱</span>
                    <span>开始时间: {formatDate(plan.startedAt)}</span>
                    <span>预计完成: {formatDate(plan.estimatedCompletion)}</span>
                  </div>
                  <div className="recruitment-results">
                    <h5>结果:</h5>
                    <span>招募人数: {plan.results.retainersRecruited.length}</span>
                    <span>总花费: {plan.results.totalCost}</span>
                  </div>
                </div>
              ))}
              
              {factionRecruitmentPlans.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🔍</span>
                  <p>暂无招募计划</p>
                  <button 
                    className="start-recruitment-btn small"
                    onClick={handleStartRecruitment}
                  >
                    开始第一个招募计划
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 事件管理 */}
        {activeTab === 'events' && (
          <div className="events-section">
            <div className="section-header">
              <h3>门客事件</h3>
            </div>
            
            <div className="events-list">
              {factionEvents.map(event => {
                const retainer = retainers.get(event.retainerId)
                return (
                  <div key={event.id} className="event-card">
                    <div className="event-header">
                      <h4>{retainer?.name || '未知门客'}</h4>
                      <span className="event-date">{formatDate(event.date)}</span>
                    </div>
                    <div className="event-content">
                      <p>{event.description}</p>
                    </div>
                  </div>
                )
              })}
              
              {factionEvents.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📅</span>
                  <p>暂无事件</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* 选中门客详情 */}
      {selectedRetainer && (
        <div className="retainer-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>门客详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedRetainer(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
      
      {/* 选中任务详情 */}
      {selectedMission && (
        <div className="mission-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>任务详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedMission(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助函数
function getRetainerTypeText(type: string): string {
  const typeTexts = {
    scholar: '文士',
    warrior: '武士',
    merchant: '商人',
    craftsman: '工匠',
    spy: '间谍',
    strategist: '策士',
    advisor: '顾问',
    diplomat: '外交家'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getRetainerQualityText(quality: string): string {
  const qualityTexts = {
    common: '普通',
    uncommon: '优秀',
    rare: '稀有',
    epic: '史诗',
    legendary: '传说'
  }
  return qualityTexts[quality as keyof typeof qualityTexts] || '未知品质'
}

function getRetainerStatusText(status: string): string {
  const statusTexts = {
    idle: '空闲',
    on_mission: '执行任务',
    resting: '休息',
    injured: '受伤',
    captured: '被俘'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getMissionTypeText(type: string): string {
  const typeTexts = {
    spy: '刺探',
    assassinate: '暗杀',
    steal: '窃取',
    negotiate: '谈判',
    train: '训练',
    research: '研究',
    trade: '贸易',
    craft: '制作'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getMissionStatusText(status: string): string {
  const statusTexts = {
    available: '可用',
    in_progress: '进行中',
    completed: '完成',
    failed: '失败',
    cancelled: '取消'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function formatDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}年${date.month}月${date.day}日`
}
